import React from 'react';
import DefaultLayout from '../../layout/DefaultLayout';
import PageTitle from '../../components/PageTitle';
import SvgIcon from '../../components/SvgIcon/SvgIcon';

/**
 * 🌟 GeneralDashboard
 *
 * Dashboard principal de bienvenida
 * usando la paleta oficial del sistema.
 *
 * ✅ Más elegante
 * ✅ Más corporativo
 * ✅ Mejor jerarquía visual
 * ✅ Mejor uso de colores del sistema
 * ✅ Diseño más premium tipo ERP SaaS
 */
const GeneralDashboard: React.FC = () => {
  return (
    <DefaultLayout>
      <div className="space-y-8">
        {/* 🔹 Título */}
        <PageTitle title="Bienvenido a Codenova POS" />

        {/* 🔹 Hero principal */}
        <section
          className="
            rounded-2xl
            border
            border-stroke
            bg-white
            p-8
            shadow-default
            transition-all
            duration-300
            dark:border-strokedark
            dark:bg-boxdark
            xl:p-10
          "
        >
          <div className="flex flex-col items-center text-center">
            {/* Icono principal */}
            <div
              className="
                mb-6
                flex
                h-22
                w-22
                items-center
                justify-center
                rounded-2xl
                border
                border-primary/20
                bg-primary/10
              "
            >
              <SvgIcon name="dashboard" className="h-10 w-10 text-primary" />
            </div>

            {/* Título principal */}
            <h1
              className="
                text-2xl
                font-bold
                text-black
                dark:text-white
                xl:text-3xl
              "
            >
              ¡Hola! 👋 Bienvenido a{' '}
              <span className="text-primary">Codenova POS</span>
            </h1>

            {/* Subtítulo */}
            <p
              className="
                mt-4
                max-w-3xl
                text-base
                leading-relaxed
                text-body
                dark:text-bodydark
              "
            >
              Tu plataforma central para la gestión empresarial moderna, segura
              y eficiente. Diseñada para ofrecer una experiencia fluida,
              productiva y adaptable a cada tipo de usuario.
            </p>

            {/* Divider elegante */}
            <div
              className="
                my-8
                h-1
                w-24
                rounded-full
                bg-primary
              "
            />
          </div>

          {/* Bloque informativo */}
          <div
            className="
              grid
              gap-6
              lg:grid-cols-3
            "
          >
            {/* Card 1 */}
            <div
              className="
                rounded-xl
                border
                border-stroke
                bg-gray-2
                p-5
                dark:border-strokedark
                dark:bg-boxdark-2
              "
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <SvgIcon name="document" className="h-5 w-5 text-primary" />
                </div>

                <h3
                  className="
                    font-semibold
                    text-black
                    dark:text-white
                  "
                >
                  Gestión Centralizada
                </h3>
              </div>

              <p
                className="
                  text-sm
                  leading-relaxed
                  text-body
                  dark:text-bodydark
                "
              >
                Integramos en una sola plataforma todos los procesos esenciales
                que mantienen tu empresa funcionando de forma organizada y
                eficiente.
              </p>
            </div>

            {/* Card 2 */}
            <div
              className="
                rounded-xl
                border
                border-stroke
                bg-gray-2
                p-5
                dark:border-strokedark
                dark:bg-boxdark-2
              "
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-success/10 p-2">
                  <SvgIcon name="user" className="h-5 w-5 text-success" />
                </div>

                <h3
                  className="
                    font-semibold
                    text-black
                    dark:text-white
                  "
                >
                  Acceso por Perfil
                </h3>
              </div>

              <p
                className="
                  text-sm
                  leading-relaxed
                  text-body
                  dark:text-bodydark
                "
              >
                Según tu perfil de usuario, tendrás acceso únicamente a los
                módulos y herramientas necesarias para tu trabajo diario.
              </p>
            </div>

            {/* Card 3 */}
            <div
              className="
                rounded-xl
                border
                border-stroke
                bg-gray-2
                p-5
                dark:border-strokedark
                dark:bg-boxdark-2
              "
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-warning/10 p-2">
                  <SvgIcon name="chart" className="h-5 w-5 text-warning" />
                </div>

                <h3
                  className="
                    font-semibold
                    text-black
                    dark:text-white
                  "
                >
                  Decisiones Inteligentes
                </h3>
              </div>

              <p
                className="
                  text-sm
                  leading-relaxed
                  text-body
                  dark:text-bodydark
                "
              >
                Potencia tus decisiones con información clara, indicadores
                relevantes y control en tiempo real.
              </p>
            </div>
          </div>

          {/* Consejo */}
          <div
            className="
              mt-8
              rounded-xl
              border
              border-primary/20
              bg-primary/5
              p-5
            "
          >
            <p
              className="
                text-sm
                text-body
                dark:text-bodydark
              "
            >
              <span className="font-semibold text-primary">💡 Consejo:</span>{' '}
              Usa el menú lateral para navegar entre módulos o el buscador
              superior para acceder rápidamente a cualquier sección del sistema.
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer
          className="
            pt-2
            text-center
            text-xs
            text-bodydark2
          "
        >
          © {new Date().getFullYear()}{' '}
          <span className="font-semibold text-primary">Codenova POS</span> —
          Desarrollado con 💙 por{' '}
          <span className="font-medium">Codenova Studios</span>
        </footer>
      </div>
    </DefaultLayout>
  );
};

export default GeneralDashboard;
